import { Component, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import styles from './ErrorBoundary.module.css'

interface ErrorBoundaryProps {
  children: ReactNode
}

interface ErrorBoundaryState {
  error: Error | null
}

function ErrorFallback() {
  const navigate = useNavigate()
  return (
    <div className={styles.fallback} role="alert">
      <p className={styles.title}>Something went wrong</p>
      <div className={styles.actions}>
        {/* Navigate to a working screen. In AppLayout this boundary is keyed by
            location.pathname, so the route change remounts it with a clean error
            state — no need to manually reset before navigating (which would
            re-render the crashing child mid-transition and rethrow). */}
        <button className={styles.button} onClick={() => navigate('/')}>
          Return home
        </button>
        <button className={styles.buttonSecondary} onClick={() => window.location.reload()}>
          Reload
        </button>
      </div>
    </div>
  )
}

/**
 * Catches a render error thrown by any descendant (a screen inside the Outlet)
 * so a single bad API shape or null deref white-screens only the screen area,
 * not the whole app. The tab bar lives OUTSIDE this boundary, so the user can
 * still navigate to a working screen. Mirrors the backend's unhandled-rejection
 * guards: never let one screen's render throw take down the whole app.
 *
 * NOTE: reset-on-navigation is handled by the CALLER keying this component by
 * `location.pathname` (see AppLayout). Error state clears when the route
 * changes; it is not auto-cleared on re-render.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error }
  }

  componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    // Console/observability only — the UI renders the fallback, never the raw stack.
    console.error('ErrorBoundary caught a render error:', error, info)
  }

  render() {
    if (this.state.error) {
      return <ErrorFallback />
    }
    return this.props.children
  }
}
